import { ProductCart as ProductCartReusable, ProductCartTranslations } from '@cdaas/reusable-components-daas';
import { useSelector } from 'react-redux';
import { LocalBundleEnvelopeWithChosenMainVariant } from '@cdaas/reusable-components-daas/dist/src/product-api-types/api-types';
import { useTranslation } from 'react-i18next';
import { Divider, Gutter } from '@gaiads/telia-react-component-library';
import { OrganizationResponse, UserResponse } from '@cdaas/api-clients/dist/asset-admin-openapi';
import { LocalProductVariant } from '@cdaas/api-clients/dist/product-service-openapi';

import { selectOrganization, selectSelectedProduct, selectSelectedUser } from '../../../store/order';

interface ProductCartProps {
  isMobile?: boolean;
}

export default function ProductCart({ isMobile }: ProductCartProps) {
  const selectedUserStore: UserResponse | null = useSelector(selectSelectedUser);
  const selectedProductStore = useSelector(selectSelectedProduct);
  const organization: OrganizationResponse | null = useSelector(selectOrganization);

  const { t } = useTranslation('orderFlow');

  const mapBundleItemsToCart = (product: LocalBundleEnvelopeWithChosenMainVariant | null): LocalProductVariant[] => {
    if (!product) return [];

    const items: LocalProductVariant[] = [];

    items.push({
      ...product.bundle.main_item.variants?.find(variant => variant.id === product.chosenMainItemVariantId),
      name: product.bundle.main_item.name,
    } as LocalProductVariant);

    const addons = product.bundle.items
      .map(addon =>
        addon.options.length
          ? ({
              ...addon.options[0],
              name: product?.addon_products?.find(addonItem => addonItem.id === addon.options[0].product_id)?.name,
            } as LocalProductVariant)
          : null,
      )
      .filter(addon => addon); // remove nulls or undefined

    items.push(...(addons as LocalProductVariant[]));
    return items;
  };

  const productCartTranslations: ProductCartTranslations = {
    yourCart: t('productCart.yourCart'),
    emptyCart: t('productCart.emptyCart'),
    emptyCartHelp: t('productCart.emptyCartHelp'),
    availabilityAvailable: t('productCart.availabilityAvailable'),
    availabilityNotAvailable: t('productCart.availabilityNotAvailable'),
    availabilityReadyToShip: t('productCart.availabilityReadyToShip'),
    availabilityFewLeft: t('productCart.availabilityFewLeft'),
    availabilityNotReadyToShip: t('productCart.availabilityNotReadyToShip'),
    totalMonthlyPrice: t('productCart.totalMonthlyPrice'),
    vat: t('productCart.vat'),
    totalPrice: t('productCart.totalPrice'),
    months: t('productCart.months'),
    includingVat: t('productCart.includingVat'),
    excludingVat: t('productCart.excludingVat'),
    paymentSingle: t('productCart.paymentSingle'),
    paymentMulti: t('productCart.paymentMulti'),
    perMonth: t('productCart.perMonth'),
    cartEmpty: t('productCart.cartEmpty'),
    currency: organization?.locale.currency.toUpperCase() || '',
    salaryDeductionEstimate: t('productCart.salaryDeductionEstimate'),
    yourAllowance: t('productCart.yourAllowance'),
    yourAllowanceExplanation: t('productCart.yourAllowanceExplanation'),
    monthlySalaryDeductionExplanation: t('productCart.monthlySalaryDeductionExplanation'),
    oneTimeSalaryDeductionExplanation: t('productCart.oneTimeSalaryDeductionExplanation'),
  };

  return (
    <>
      {isMobile && (
        <>
          <Gutter size="lg" />
          <Divider />
          <Gutter />
        </>
      )}
      <ProductCartReusable
        cartItems={mapBundleItemsToCart(selectedProductStore)}
        bundle={selectedProductStore}
        translations={productCartTranslations}
        showEmptyCartText={false}
        allowanceType={selectedUserStore?.allowance_type}
        allowance={selectedUserStore?.allowance}
        locale={organization?.locale.language}
      />
    </>
  );
}
